import { DEFAULT_POLICY, resolvePolicy } from "@/modules/lernpfad/engine"
import type { ComposerPolicy, ResolvedPolicy } from "@/modules/lernpfad/engine"

/**
 * Which policy fields belong to which parameter cluster.
 *
 * Two features need this and neither works without it: the section rail's
 * "n changed" badge per cluster, and the "written by {framework}" marker that
 * links a framework selection back to the controls it moved. `countOverrides`
 * answers the same question globally; this scopes it.
 *
 * The map is the one piece of bookkeeping that can silently rot — add a policy
 * field, forget to list it, and its slider's badge never lights even though the
 * knob works. `cluster-map.test.ts` asserts the paths cover every leaf in
 * `DEFAULT_POLICY` exactly once, so the omission fails CI instead of shipping.
 */

export type ClusterId =
    | "budget"
    | "priority"
    | "evidence"
    | "duties"
    | "gaps"
    | "sources"
    | "sequencing"
    | "mix"
    | "cost"
    | "governance"

/** Dotted prefixes owned by each cluster. A prefix claims everything beneath it. */
export const CLUSTER_PATHS: Record<ClusterId, string[]> = {
    budget: ["budget"],
    priority: [
        "ranking.originWeight",
        "ranking.denominator",
        "ranking.minHours",
        "ranking.minUsd",
        "ranking.reasonRank",
    ],
    evidence: ["evidence"],
    duties: ["obligations", "targetProfile"],
    gaps: ["gaps", "ranking.tooBasicTolerance"],
    sources: ["sources"],
    sequencing: ["scheduling", "reinforcement"],
    mix: ["mix"],
    cost: ["cost"],
    // `version` is not a setting; it is the migration marker. It belongs
    // somewhere for the coverage test, and governance is where the page
    // explains that the engine is deterministic.
    governance: ["version"],
}

export const CLUSTER_ORDER: ClusterId[] = [
    "budget",
    "priority",
    "evidence",
    "duties",
    "gaps",
    "sources",
    "sequencing",
    "mix",
    "cost",
    "governance",
]

/** Every leaf path in a resolved policy, dotted. */
export function policyLeafPaths(policy: ResolvedPolicy = DEFAULT_POLICY): string[] {
    const paths: string[] = []
    const walk = (value: unknown, prefix: string) => {
        if (value !== null && typeof value === "object") {
            for (const key of Object.keys(value as Record<string, unknown>)) {
                walk((value as Record<string, unknown>)[key], prefix ? `${prefix}.${key}` : key)
            }
            return
        }
        paths.push(prefix)
    }
    walk(policy, "")
    return paths
}

function readPath(source: unknown, path: string): unknown {
    return path
        .split(".")
        .reduce<unknown>(
            (value, key) =>
                value !== null && typeof value === "object"
                    ? (value as Record<string, unknown>)[key]
                    : undefined,
            source,
        )
}

/** The cluster a leaf path belongs to — longest matching prefix wins. */
export function clusterFor(path: string): ClusterId | undefined {
    let best: { cluster: ClusterId; length: number } | undefined
    for (const cluster of CLUSTER_ORDER) {
        for (const prefix of CLUSTER_PATHS[cluster]) {
            if (path !== prefix && !path.startsWith(`${prefix}.`)) continue
            if (!best || prefix.length > best.length) best = { cluster, length: prefix.length }
        }
    }
    return best?.cluster
}

/**
 * How many leaf settings differ from standard, per cluster.
 *
 * Compares the RESOLVED policies rather than the raw override object, so a
 * framework preset and a hand-moved slider count identically — which is what
 * the badge means to a reader.
 */
export function overridesByCluster(policy: ComposerPolicy | undefined): Record<ClusterId, number> {
    const counts = Object.fromEntries(CLUSTER_ORDER.map((id) => [id, 0])) as Record<ClusterId, number>
    if (!policy) return counts

    const resolved = resolvePolicy(policy)
    for (const path of policyLeafPaths()) {
        if (readPath(resolved, path) === readPath(DEFAULT_POLICY, path)) continue
        const cluster = clusterFor(path)
        if (cluster) counts[cluster] += 1
    }
    return counts
}
